/*
 * Sign in through the SkySlope Forms widget when the browser blocks third-party cookies (AP-3360).
 *
 * Forms reaches the sender by navigating the widget's iframe to us. In a browser that blocks
 * third-party cookies, the normal Okta redirect inside that frame dead-ends on Okta's "Cookies are
 * required" page. So when Forms tells us it runs on a widget token (the `sessionless` hint next to a
 * `frameOrigin`, see utils/sessionless-hint.ts), we do not redirect. We ask the widget instead:
 *
 *   sender -> widget  { status: 'digisign-auth-required' }
 *   widget -> sender  { status: 'digisign-set-token', token }
 *   sender -> widget  { status: 'digisign-token-installed', ok, exp }
 *
 * The widget can also send 'digisign-set-token' later on its own to renew the session, and
 * 'digisign-clear-token' when the host signs the user out ('digisign-token-cleared' answers).
 * If no token comes in time we send 'digisign-auth-failed'. When the user signs out inside
 * DigiSign we send 'digisign-signed-out', so the widget stops handing us tokens.
 *
 * Every message we send goes to the exact widget origin, never '*'. We only accept messages from
 * that origin and only from our parent window. Tokens are never logged.
 *
 * Each ask records one `widget_sign_in_completed` event with its outcome (AP-4201). A token the
 * widget sends while no ask is waiting is a renewal, and records nothing. The one exception: the
 * first token that installs after an ask gave up records `widget_sign_in_completed_late`.
 */
import { isSessionless } from '@skyslope/auth-config';
import type { UserManager } from '@skyslope/auth-js';

import { trackWidgetEvent, type WidgetSignInFailure } from '../lib/widget-analytics';
import { captureFrameOrigin, FRAME_ORIGIN_PARAM, getFrameOrigin } from '../utils/frame-origin';
import { captureSessionlessHint, isSessionlessHinted, SESSIONLESS_PARAM } from '../utils/sessionless-hint';

const DIGISIGN_AUTH_REQUIRED = 'digisign-auth-required';
const DIGISIGN_SET_TOKEN = 'digisign-set-token';
const DIGISIGN_TOKEN_INSTALLED = 'digisign-token-installed';
const DIGISIGN_CLEAR_TOKEN = 'digisign-clear-token';
const DIGISIGN_TOKEN_CLEARED = 'digisign-token-cleared';
const DIGISIGN_AUTH_FAILED = 'digisign-auth-failed';
const DIGISIGN_SIGNED_OUT = 'digisign-signed-out';

// How long we wait for the widget to send a token. The widget gives the host's getToken
// 30 seconds, so wait a little longer than that.
export const TOKEN_WAIT_MS = 35 * 1000;

/** True when we are framed by the widget and Forms said this frame runs on a widget token. */
export function isWidgetTokenSession(): boolean {
  // getFrameOrigin() is null when we are not framed.
  return getFrameOrigin() != null && isSessionlessHinted();
}

function postToWidget(message: Record<string, unknown>): void {
  const origin = getFrameOrigin();
  if (origin == null) return;
  window.parent.postMessage(message, origin);
}

// Reads a JWT's claims. Decode only: the APIs check the token, we never trust it here.
function claimsOf(jwt: string): Record<string, unknown> | undefined {
  try {
    return JSON.parse(atob(jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return undefined;
  }
}

// The token's user (`uid`, else `sub`), plus the impersonated user and the DigiSign account, which
// can change while `uid` stays. The account claims are read in useUserInfo's order.
function userOf(jwt: string): string | undefined {
  const claims = claimsOf(jwt);
  const user = claims?.uid ?? claims?.sub;
  if (!claims || typeof user !== 'string' || user === '') return undefined;
  const account =
    claims.AccountId ||
    claims.account_id ||
    claims.digisign_prime_account_id ||
    claims.digisign_forms_account_id ||
    claims.digisign_fnf_account_id;
  return JSON.stringify([user, claims.delegated_user_id ?? null, account ?? null]);
}

function expiryOf(jwt: string): number | undefined {
  const exp = claimsOf(jwt)?.exp;
  return typeof exp === 'number' && Number.isFinite(exp) ? exp : undefined;
}

// How an ask for a token ended: installed, a failure reason, or the user signed out meanwhile.
type TokenWaitEnd = 'installed' | WidgetSignInFailure | 'signed_out';
// Ends the ask in progress; null when no ask is waiting.
let settleTokenWait: ((end: TokenWaitEnd) => void) | null = null;
// The ask in progress, so a second caller waits for the same answer instead of asking again.
let pendingRequest: Promise<boolean> | null = null;
let listening = false;
// True once the user signed out inside DigiSign on this page, or the widget cleared the token. From
// then on we neither ask the widget for a token nor take one it sends: a renewal already on its way,
// or SecureRoute asking again once the session is gone, would sign the same user straight back in.
let signedOutOfWidget = false;
// The imports in progress, one at a time, and the newest token sent meanwhile, imported next. Only
// the newest counts: when the host switches users mid-import, the previous user must not stay
// signed in.
let importing: Promise<void> | null = null;
let nextToken: string | null = null;
// Whether this page has installed a token, and that token's user (undefined when unreadable), so a
// switch to another user is noticed.
let installed = false;
let installedUser: string | undefined;
// When an ask last gave up waiting (Date.now()), until a token installs or a new ask starts.
// Not time-capped: seconds_late lets analysts tell a slow first token from a much later renewal.
let gaveUpAt: number | null = null;

function installToken(um: UserManager, token: string): Promise<void> {
  nextToken = token;
  importing ??= importNewest(um).finally(() => {
    importing = null;
  });
  return importing;
}

async function importNewest(um: UserManager): Promise<void> {
  while (nextToken != null && !signedOutOfWidget) {
    const token = nextToken;
    nextToken = null;
    await runImport(um, token);
  }
}

async function runImport(um: UserManager, token: string): Promise<void> {
  let threw = false;
  const ok = await um.importToken(token).catch(() => {
    threw = true;
    return false;
  });
  // Signed out or cleared meanwhile: that answer has gone to the widget, so this one must not follow.
  if (signedOutOfWidget) return;
  // Report the expiry of the token the session really uses. auth-js exchanges the widget's
  // token, so it is not the one the widget sent.
  const sessionToken = ok ? await um.getAccessToken().catch(() => null) : null;
  if (signedOutOfWidget) return;
  const exp = sessionToken ? expiryOf(sessionToken) : undefined;
  postToWidget({ status: DIGISIGN_TOKEN_INSTALLED, ok, exp });
  // While a newer token is queued, neither outcome ends the wait: that one decides it, so the page
  // does not render for a user the host has already switched away from.
  if (nextToken == null) settleTokenWait?.(ok ? 'installed' : threw ? 'error' : 'token_rejected');
  if (!ok) return;
  // The user got in after the ask gave up and recorded no_token. Record that once.
  if (gaveUpAt != null) {
    const secondsLate = Math.round((Date.now() - gaveUpAt) / 1000);
    gaveUpAt = null;
    trackWidgetEvent({
      name: 'widget_sign_in_completed_late',
      properties: { widget_origin: getFrameOrigin() ?? '', seconds_late: secondsLate },
    });
  }
  // Cached settings and mounted views belong to the user signed in before. Reload unless both users
  // are known and the same, so none of it stays on screen; the reload asks the widget again.
  const user = sessionToken ? userOf(sessionToken) : undefined;
  if (installed && (user === undefined || user !== installedUser)) {
    window.location.reload();
    return;
  }
  installed = true;
  installedUser = user;
}

/**
 * Drops this frame's session storage, for when auth-js could not remove an imported token. Stores
 * the frameOrigin and sessionless hint again, so a reload of the frame whose URL lost them is still
 * a widget token session.
 */
export function clearFrameSession(): void {
  const frameOrigin = getFrameOrigin();
  const hinted = isSessionlessHinted();
  try {
    sessionStorage.clear();
  } catch {
    // Storage blocked: nothing we can remove.
    return;
  }
  if (frameOrigin == null) return;
  const params = new URLSearchParams({ [FRAME_ORIGIN_PARAM]: frameOrigin });
  if (hinted) params.set(SESSIONLESS_PARAM, '1');
  captureFrameOrigin(`?${params}`);
  captureSessionlessHint(`?${params}`);
}

// auth-js marks an imported token in storage and keeps the mark when it cannot remove the token.
// clearImportedToken's own result cannot tell us this: it is also false when nothing was imported.
// Storage that cannot be read cannot hold a readable token either, so that counts as not stored.
function importedTokenStillStored(): boolean {
  try {
    return isSessionless();
  } catch {
    return false;
  }
}

/**
 * Removes any imported token from this frame. If auth-js could not remove it, drops the frame's
 * session storage instead, so the old user cannot be signed back in. Returns false in that case.
 */
export async function dropImportedToken(um: UserManager): Promise<boolean> {
  await um.clearImportedToken().catch(() => false);
  if (!importedTokenStillStored()) return true;
  clearFrameSession();
  return false;
}

/**
 * The user signed out inside DigiSign in a widget token session. Drop the imported session here and
 * tell the widget, so it stops renewing and hands us no more tokens until the host signs someone in
 * again. No Okta sign-out: there is no Okta session in this frame, and Okta's sign-out would dead-end.
 */
export async function signOutOfWidgetSession(um: UserManager): Promise<void> {
  signedOutOfWidget = true;
  // A token ask still waiting (the page asks as it loads) ends here, without a token. It must not
  // later tell the widget it failed: the user signed out.
  settleTokenWait?.('signed_out');
  // A token import already running would store its token after our drop. Let it finish first.
  await importing;
  await dropImportedToken(um);
  postToWidget({ status: DIGISIGN_SIGNED_OUT });
}

// The widget's sign-out: drop the session, tell the widget, and reload so no data stays on screen.
async function signOutForWidget(um: UserManager): Promise<void> {
  // Take no more tokens until the reload: not one queued behind a running import, and not one the
  // widget sends before the reload lands. A token ask still waiting ends here, without a token.
  signedOutOfWidget = true;
  settleTokenWait?.('signed_out');
  // An import running now may store its token after our first drop; it is dropped again below.
  const running = importing;
  // Answer right away, so an import that hangs cannot hold up the widget's clear.
  const ok = await dropImportedToken(um);
  postToWidget({ status: DIGISIGN_TOKEN_CLEARED, ok });
  if (running) {
    await running;
    await dropImportedToken(um);
  }
  // The page still shows the old user's data. Reload so none of it stays.
  window.location.reload();
}

/** Starts taking tokens from the widget. Does nothing outside a widget token session. */
export function listenForWidgetTokens(um: UserManager): void {
  if (listening || !isWidgetTokenSession()) return;
  listening = true;

  window.addEventListener('message', (event: MessageEvent) => {
    // event.origin is always a string, so this also rejects everything when no origin is stored.
    if (event.origin !== getFrameOrigin() || event.source !== window.parent) return;

    let data: { status?: unknown; token?: unknown } | null;
    try {
      data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
    } catch {
      return;
    }

    if (data?.status === DIGISIGN_SET_TOKEN && typeof data.token === 'string' && data.token !== '') {
      if (signedOutOfWidget) return;
      void installToken(um, data.token);
    } else if (data?.status === DIGISIGN_CLEAR_TOKEN) {
      void signOutForWidget(um);
    }
  });
}

/**
 * Takes the place of the Okta redirect in a widget token session: asks the widget for a token
 * and waits. Returns true once a token is installed. On a timeout it tells the widget and stays
 * put, because an Okta redirect would only dead-end on the cookie page.
 */
export function requestTokenFromWidget(um: UserManager): Promise<boolean> {
  if (signedOutOfWidget || !isWidgetTokenSession()) return Promise.resolve(false);
  listenForWidgetTokens(um);
  // Already asked and still waiting: the answer to that ask covers this caller too.
  pendingRequest ??= askWidgetAndWait().finally(() => {
    pendingRequest = null;
  });
  return pendingRequest;
}

// Posts digisign-auth-required, waits up to TOKEN_WAIT_MS for the token to install, and posts
// digisign-auth-failed if it does not (unless the user signed out meanwhile).
async function askWidgetAndWait(): Promise<boolean> {
  // This ask reports its own result, so an earlier give-up is no longer waiting for a late token.
  gaveUpAt = null;
  const answer = new Promise<TokenWaitEnd>((resolve) => {
    const timer = setTimeout(() => {
      gaveUpAt = Date.now();
      resolve('no_token');
    }, TOKEN_WAIT_MS);
    settleTokenWait = (end) => {
      clearTimeout(timer);
      resolve(end);
    };
  });
  postToWidget({ status: DIGISIGN_AUTH_REQUIRED });
  const end = await answer;
  settleTokenWait = null;
  const ok = end === 'installed';
  // A sign-out is not a sign-in result, so it records nothing. This also covers a sign-out that
  // runs after the wait settled but before we got here. Sign-outs set signedOutOfWidget before
  // they settle with 'signed_out', so end is a sign-in result inside this block.
  if (!signedOutOfWidget) {
    const reason = end === 'installed' ? null : (end as WidgetSignInFailure);
    trackWidgetEvent({
      name: 'widget_sign_in_completed',
      properties: { success: reason === null, reason, widget_origin: getFrameOrigin() ?? '' },
    });
  }
  if (!ok && !signedOutOfWidget) postToWidget({ status: DIGISIGN_AUTH_FAILED });
  return ok;
}
